import React, { useEffect, useMemo, useState } from 'react';
import { ImportOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, DatePicker, Descriptions, Empty, Input, InputNumber, Popconfirm, Row, Segmented, Select, Space, Statistic, Tag, Tooltip, Typography, message } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import type { EChartsOption } from 'echarts';
import PageContainer from '@/components/PageContainer';
import ChartCard from '@/components/ChartCard';
import Loading from '@/components/Loading';
import ErrorFallback from '@/components/ErrorFallback';
import MarketDayTypeCards from '@/components/MarketDayTypeCards';
import TradingDiaryImportModal from '@/components/TradingDiaryImportModal';
import { useStations } from '@/hooks/useStations';
import { useCompleteTradingDiaryNote, useSaveTradingDiaryNote, useTradingDiary } from '@/api/tradingDiary';
import { useMarketDayTypeCards } from '@/api/marketDayTypes';
import {
  useAddTextEvidence,
  useArchiveDecisionNode,
  useDeleteEvidence,
  useSaveDecisionNode,
  useTradingDecisionTrace,
  useUploadEvidenceImage,
} from '@/api/tradingTraceability';
import { BASE_DATAZOOM, format2 } from '@/utils/charts';
import type { TradingDecisionEvidence, TradingDecisionNode, TradingDiaryDay, TradingDiaryNote, TradingDiaryNoteStatus } from '@/types/api';

const { RangePicker } = DatePicker;
const { TextArea } = Input;

const DEFAULT_RANGE: [Dayjs, Dayjs] = [dayjs().subtract(6, 'day'), dayjs()];
const DEFAULT_SELECTED_DATE = dayjs().subtract(1, 'day').format('YYYY-MM-DD');

interface DiaryDraft {
  preJudgement: string;
  intradayAction: string;
  deviationReason: string;
  selfScore: number | null;
}

interface DecisionNodeDraft {
  evidence_interpretation: string;
  market_judgement: string;
  action_taken: string;
  risk_note: string;
  result_review: string;
  self_score: number | null;
}

interface EvidenceDraft {
  title: string;
  content_text: string;
  tags: string;
}

type DiaryMode = 'view' | 'fill';

const EMPTY_DRAFT: DiaryDraft = {
  preJudgement: '',
  intradayAction: '',
  deviationReason: '',
  selfScore: null,
};

function noteToDraft(note?: TradingDiaryNote): DiaryDraft {
  if (!note) return EMPTY_DRAFT;
  return {
    preJudgement: note.pre_judgement ?? '',
    intradayAction: note.intraday_action ?? '',
    deviationReason: note.deviation_reason ?? '',
    selfScore: note.self_score ?? null,
  };
}

function buildNotePayload(draft: DiaryDraft) {
  return {
    pre_judgement: draft.preJudgement,
    intraday_action: draft.intradayAction,
    deviation_reason: draft.deviationReason,
    self_score: draft.selfScore,
  };
}

function decisionNodeKey(node: Pick<TradingDecisionNode, 'record_date' | 'target_date' | 'node_code'>) {
  return `${node.record_date}|${node.target_date}|${node.node_code}`;
}

function nodeToDraft(node: TradingDecisionNode): DecisionNodeDraft {
  return {
    evidence_interpretation: node.evidence_interpretation ?? '',
    market_judgement: node.market_judgement ?? '',
    action_taken: node.action_taken ?? '',
    risk_note: node.risk_note ?? '',
    result_review: node.result_review ?? '',
    self_score: node.self_score ?? null,
  };
}

function getNoteStatusMeta(status?: TradingDiaryNoteStatus) {
  if (status === 'completed') return { text: '已完成', color: 'green' };
  if (status === 'draft') return { text: '草稿', color: 'blue' };
  return { text: '待填写', color: 'default' };
}

function getWeatherLabels(day?: TradingDiaryDay) {
  const labels = day?.weather_overview.weather_labels;
  return Array.isArray(labels) ? labels : [];
}

function getWeatherTagColor(label: string) {
  if (label.includes('缺失')) return 'default';
  if (label.includes('高辐照') || label.includes('大风') || label.includes('中高风')) return 'gold';
  if (label.includes('低') || label.includes('弱')) return 'blue';
  return 'geekblue';
}

function getDecisionNodeStatusMeta(status?: TradingDecisionNode['status']) {
  if (status === 'archived') return { text: '已归档', color: 'green' };
  return { text: '草稿', color: 'blue' };
}

function getEvidenceImageSrc(item: TradingDecisionEvidence, station: string) {
  return item.content_url ?? `/api/v1/trading-diary/decision-node/evidence/${item.id}/file?station=${encodeURIComponent(station)}`;
}

function hasDecisionText(node: TradingDecisionNode) {
  return Boolean(
    node.evidence_interpretation?.trim()
    || node.market_judgement?.trim()
    || node.action_taken?.trim()
    || node.risk_note?.trim()
    || node.result_review?.trim()
  );
}

function MetricLine({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, lineHeight: 1.8 }}>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <Typography.Text strong>
        {value}
        {unit ? <Typography.Text type="secondary"> {unit}</Typography.Text> : null}
      </Typography.Text>
    </div>
  );
}

function buildPriceOption(day?: TradingDiaryDay): EChartsOption {
  const curve = day?.price_curve ?? [];
  return {
    tooltip: { trigger: 'axis' },
    legend: { top: 0, data: ['实时均价', '日前价格'] },
    dataZoom: BASE_DATAZOOM,
    grid: { left: 52, right: 20, top: 40, bottom: 76 },
    xAxis: { type: 'category', data: curve.map((p) => p.time), axisLabel: { fontSize: 11 } },
    yAxis: { type: 'value', name: '元/MWh', splitLine: { lineStyle: { color: '#eef2f7' } } },
    series: [
      {
        name: '实时均价',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: curve.map((p) => p.price_rt_avg),
        lineStyle: { color: '#2563eb', width: 2 },
      },
      {
        name: '日前价格',
        type: 'line',
        smooth: true,
        showSymbol: false,
        data: curve.map((p) => p.price_da),
        lineStyle: { color: '#f97316', width: 2 },
      },
    ],
  };
}

function buildExecutionOption(day?: TradingDiaryDay): EChartsOption {
  const curve = day?.execution_curve ?? [];
  return {
    tooltip: { trigger: 'axis' },
    legend: { top: 0, data: ['AGC实际', '日前申报', '储能功率', '超短期预测', '可用功率'] },
    dataZoom: BASE_DATAZOOM,
    grid: { left: 52, right: 20, top: 40, bottom: 76 },
    xAxis: { type: 'category', data: curve.map((p) => p.time), axisLabel: { fontSize: 11 } },
    yAxis: { type: 'value', name: 'MW', splitLine: { lineStyle: { color: '#eef2f7' } } },
    series: [
      {
        name: 'AGC实际',
        type: 'line',
        showSymbol: false,
        data: curve.map((p) => p.agc_total_actual_power),
        lineStyle: { color: '#059669', width: 2 },
      },
      {
        name: '日前申报',
        type: 'line',
        showSymbol: false,
        data: curve.map((p) => p.epower),
        lineStyle: { color: '#7c3aed', width: 2 },
      },
      {
        name: '超短期预测',
        type: 'line',
        showSymbol: false,
        data: curve.map((p) => p.ultra_short_term_pred),
        lineStyle: { color: '#0ea5e9', width: 2, type: 'dashed' },
      },
      {
        name: '可用功率',
        type: 'line',
        showSymbol: false,
        data: curve.map((p) => p.pv_available_power),
        lineStyle: { color: '#f59e0b', width: 2, type: 'dashed' },
      },
      {
        name: '储能功率',
        type: 'bar',
        data: curve.map((p) => p.battery_actual_power),
        itemStyle: { color: '#64748b' },
      },
    ],
  };
}

const TradingDiaryPage: React.FC = () => {
  const { data: stationData, isLoading: stationsLoading } = useStations();
  const stations = stationData?.stations ?? [];
  const [station, setStation] = useState('');
  const [range, setRange] = useState<[Dayjs, Dayjs]>(DEFAULT_RANGE);
  const [selectedDate, setSelectedDate] = useState(DEFAULT_SELECTED_DATE);
  const [draft, setDraft] = useState<DiaryDraft>(EMPTY_DRAFT);
  const [nodeDrafts, setNodeDrafts] = useState<Record<string, DecisionNodeDraft>>({});
  const [evidenceDrafts, setEvidenceDrafts] = useState<Record<string, EvidenceDraft>>({});
  const [selectedNodeKey, setSelectedNodeKey] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState('');
  const [editMode, setEditMode] = useState(true);
  const [mode, setMode] = useState<DiaryMode>('fill');
  const [modeTouched, setModeTouched] = useState(false);
  const showManualReviewPanel = false;
  const [importModalOpen, setImportModalOpen] = useState(false);

  useEffect(() => {
    if (!station && stations.length > 0) {
      setStation(stations[0]);
    }
  }, [station, stations]);

  const query = useTradingDiary({
    station,
    startDate: range[0].format('YYYY-MM-DD'),
    endDate: range[1].format('YYYY-MM-DD'),
  });
  const dayTypeQuery = useMarketDayTypeCards({
    startDate: range[0].format('YYYY-MM-DD'),
    endDate: range[1].format('YYYY-MM-DD'),
  });
  const traceQuery = useTradingDecisionTrace({
    station,
    startDate: range[0].format('YYYY-MM-DD'),
    endDate: range[1].format('YYYY-MM-DD'),
  });
  const saveNoteMutation = useSaveTradingDiaryNote();
  const completeNoteMutation = useCompleteTradingDiaryNote();
  const saveDecisionNodeMutation = useSaveDecisionNode();
  const archiveDecisionNodeMutation = useArchiveDecisionNode();
  const addTextEvidenceMutation = useAddTextEvidence();
  const uploadEvidenceImageMutation = useUploadEvidenceImage();
  const deleteEvidenceMutation = useDeleteEvidence();

  const days = query.data?.data.days ?? [];
  const traceDays = traceQuery.data?.data.days ?? [];
  const selectedDay = useMemo(
    () => days.find((d) => d.date === selectedDate) ?? days[0],
    [days, selectedDate]
  );
  const selectedTraceDay = useMemo(
    () => traceDays.find((d) => d.record_date === selectedDate) ?? traceDays[0],
    [traceDays, selectedDate]
  );
  const selectedTraceNodes = selectedTraceDay?.nodes ?? [];
  const archivedTraceNodes = useMemo(
    () => selectedTraceNodes.filter((node) => node.status === 'archived'),
    [selectedTraceNodes]
  );
  const viewTraceNodes = archivedTraceNodes.length > 0 ? archivedTraceNodes : selectedTraceNodes;
  const imageEvidenceCount = viewTraceNodes.reduce(
    (sum, node) => sum + (node.evidence_items ?? []).filter((item) => item.evidence_type === 'image').length,
    0
  );
  const selectedDecisionNode = useMemo(() => {
    const nodes = selectedTraceDay?.nodes ?? [];
    if (!nodes.length) return undefined;
    return nodes.find((node) => decisionNodeKey(node) === selectedNodeKey) ?? nodes[0];
  }, [selectedTraceDay, selectedNodeKey]);

  useEffect(() => {
    if (days.length > 0 && !days.some((d) => d.date === selectedDate)) {
      setSelectedDate(days[0].date);
      setModeTouched(false);
    }
  }, [days, selectedDate]);

  useEffect(() => {
    if (!modeTouched) {
      setMode(archivedTraceNodes.length > 0 ? 'view' : 'fill');
    }
  }, [archivedTraceNodes.length, modeTouched]);

  useEffect(() => {
    if (!selectedDay) return;
    const nextDraft = noteToDraft(selectedDay.note);
    setDraft(nextDraft);
    setEditMode(selectedDay.note?.status !== 'completed');
    setLastSavedAt('');
  }, [selectedDay]);

  useEffect(() => {
    if (!selectedTraceDay?.nodes) return;
    const nextDrafts: Record<string, DecisionNodeDraft> = {};
    const nextEvidenceDrafts: Record<string, EvidenceDraft> = {};
    selectedTraceDay.nodes.forEach((node) => {
      const key = decisionNodeKey(node);
      nextDrafts[key] = nodeToDraft(node);
      nextEvidenceDrafts[key] = { title: '', content_text: '', tags: '' };
    });
    setNodeDrafts(nextDrafts);
    setEvidenceDrafts(nextEvidenceDrafts);
  }, [selectedTraceDay]);

  useEffect(() => {
    const nodes = selectedTraceDay?.nodes ?? [];
    if (!nodes.length) {
      if (selectedNodeKey) setSelectedNodeKey('');
      return;
    }
    if (!nodes.some((node) => decisionNodeKey(node) === selectedNodeKey)) {
      setSelectedNodeKey(decisionNodeKey(nodes[0]));
    }
  }, [selectedTraceDay, selectedNodeKey]);

  const updateDraft = (patch: Partial<DiaryDraft>) => {
    if (!selectedDay) return;
    const next = { ...draft, ...patch };
    setDraft(next);
    setLastSavedAt('');
  };

  const updateNodeDraft = (node: TradingDecisionNode, patch: Partial<DecisionNodeDraft>) => {
    const key = decisionNodeKey(node);
    setNodeDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? nodeToDraft(node)), ...patch },
    }));
  };

  const updateEvidenceDraft = (node: TradingDecisionNode, patch: Partial<EvidenceDraft>) => {
    const key = decisionNodeKey(node);
    setEvidenceDrafts((prev) => ({
      ...prev,
      [key]: { ...(prev[key] ?? { title: '', content_text: '', tags: '' }), ...patch },
    }));
  };

  const handleSaveDecisionNode = async (node: TradingDecisionNode) => {
    if (!station) return;
    const key = decisionNodeKey(node);
    const nodeDraft = nodeDrafts[key] ?? nodeToDraft(node);
    try {
      await saveDecisionNodeMutation.mutateAsync({
        station,
        record_date: node.record_date,
        target_date: node.target_date,
        node_code: node.node_code,
        ...nodeDraft,
      });
      message.success(`已保存 ${node.node_title}`);
      await traceQuery.refetch();
    } catch {
      message.error('决策节点保存失败');
    }
  };

  const handleArchiveDecisionNode = async (node: TradingDecisionNode) => {
    if (!station) return;
    const key = decisionNodeKey(node);
    const nodeDraft = nodeDrafts[key] ?? nodeToDraft(node);
    try {
      await saveDecisionNodeMutation.mutateAsync({
        station,
        record_date: node.record_date,
        target_date: node.target_date,
        node_code: node.node_code,
        ...nodeDraft,
      });
      await archiveDecisionNodeMutation.mutateAsync({
        station,
        record_date: node.record_date,
        target_date: node.target_date,
        node_code: node.node_code,
      });
      message.success(`已保存并归档 ${node.node_title}`);
      await traceQuery.refetch();
    } catch {
      message.error('决策节点保存并归档失败');
    }
  };

  const handleAddTextEvidence = async (node: TradingDecisionNode) => {
    if (!station) return;
    const key = decisionNodeKey(node);
    const evidenceDraft = evidenceDrafts[key] ?? { title: '', content_text: '', tags: '' };
    try {
      await addTextEvidenceMutation.mutateAsync({
        station,
        record_date: node.record_date,
        target_date: node.target_date,
        node_code: node.node_code,
        evidence_type: 'chat_text',
        title: evidenceDraft.title,
        content_text: evidenceDraft.content_text,
        tags: evidenceDraft.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
      });
      updateEvidenceDraft(node, { title: '', content_text: '', tags: '' });
      message.success(`已添加 ${node.node_title} 证据`);
      await traceQuery.refetch();
    } catch {
      message.error('文字证据添加失败');
    }
  };

  const handleUploadImageEvidence = async (node: TradingDecisionNode, file?: File) => {
    if (!station || !file) return;
    const key = decisionNodeKey(node);
    const evidenceDraft = evidenceDrafts[key] ?? { title: '', content_text: '', tags: '' };
    try {
      await uploadEvidenceImageMutation.mutateAsync({
        station,
        record_date: node.record_date,
        target_date: node.target_date,
        node_code: node.node_code,
        title: evidenceDraft.title || file.name,
        tags: evidenceDraft.tags.split(',').map((tag) => tag.trim()).filter(Boolean),
        file,
      });
      message.success(`已上传 ${node.node_title} 图片证据`);
      await traceQuery.refetch();
    } catch {
      message.error('图片证据上传失败');
    }
  };

  const handleDeleteEvidence = async (evidenceId: number) => {
    if (!station) return;
    try {
      await deleteEvidenceMutation.mutateAsync({ station, evidenceId });
      message.success('证据已删除');
      await traceQuery.refetch();
    } catch {
      message.error('证据删除失败');
    }
  };

  const handleSaveDraft = async () => {
    if (!selectedDay || !station) return;
    try {
      await saveNoteMutation.mutateAsync({
        station,
        date: selectedDay.date,
        ...buildNotePayload(draft),
      });
      const savedAt = dayjs().format('HH:mm:ss');
      setLastSavedAt(savedAt);
      message.success(`已保存 ${selectedDay.date} 草稿`);
      await query.refetch();
    } catch {
      message.error('复盘草稿保存失败');
    }
  };

  const handleCompleteDiary = async () => {
    if (!selectedDay || !station) return;
    try {
      await completeNoteMutation.mutateAsync({
        station,
        date: selectedDay.date,
        ...buildNotePayload(draft),
      });
      setLastSavedAt('');
      setEditMode(false);
      message.success(`已完成 ${selectedDay.date} 复盘`);
      await query.refetch();
    } catch {
      message.error('复盘完成保存失败');
    }
  };

  if (stationsLoading) {
    return <PageContainer title="交易策略留痕"><Loading /></PageContainer>;
  }

  return (
    <PageContainer title="交易策略留痕">
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          style={{ width: 220 }}
          value={station || undefined}
          placeholder="选择场站"
          options={stations.map((s) => ({ label: s, value: s }))}
          onChange={setStation}
        />
        <RangePicker
          value={range}
          allowClear={false}
          onChange={(value) => {
            if (value?.[0] && value?.[1]) {
              setRange([value[0], value[1]]);
            }
          }}
        />
        <Button onClick={() => { query.refetch(); traceQuery.refetch(); dayTypeQuery.refetch(); }}>刷新</Button>
        <Button
          icon={<ImportOutlined />}
          disabled={!station}
          onClick={() => setImportModalOpen(true)}
        >
          导入复盘
        </Button>
        <Segmented
          value={mode}
          options={[
            { label: '查看', value: 'view' },
            { label: '填写', value: 'fill' },
          ]}
          onChange={(value) => {
            setMode(value as DiaryMode);
            setModeTouched(true);
          }}
        />
      </Space>

      <MarketDayTypeCards
        cards={dayTypeQuery.data?.data.cards ?? []}
        selectedDate={selectedDate}
        loading={dayTypeQuery.isLoading}
        error={dayTypeQuery.error instanceof Error ? dayTypeQuery.error : null}
        onSelectDate={setSelectedDate}
        onRetry={() => dayTypeQuery.refetch()}
      />

      {query.error ? (
        <ErrorFallback message={query.error instanceof Error ? query.error.message : '交易日志加载失败'} onRetry={() => query.refetch()} />
      ) : query.isLoading ? (
        <Loading />
      ) : !selectedDay ? (
        <Empty description="暂无交易日志数据" />
      ) : (
        <Row gutter={[16, 16]} align="top">
          <Col xs={24} lg={6}>
            <div style={{ display: 'grid', gap: 8 }}>
              {days.map((day) => {
                const status = getNoteStatusMeta(day.note?.status);
                return (
                  <Card
                    key={day.date}
                    size="small"
                    onClick={() => setSelectedDate(day.date)}
                    style={{
                      cursor: 'pointer',
                      borderColor: day.date === selectedDay.date ? '#2563eb' : '#d9d9d9',
                      borderWidth: day.date === selectedDay.date ? 2 : 1,
                    }}
                  >
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                        <Typography.Text strong>{day.date}</Typography.Text>
                        <Tag color={status.color}>{status.text}</Tag>
                      </Space>
                      <Space wrap>
                        {day.deviation_summary?.has_deviation ? <Tag color="red">有偏差</Tag> : <Tag color="green">无明显偏差</Tag>}
                        <Typography.Text>{day.day_type}</Typography.Text>
                      </Space>
                      <Space wrap>
                        {day.labels.map((label) => <Tag key={label}>{label}</Tag>)}
                      </Space>
                    </Space>
                  </Card>
                );
              })}
            </div>
          </Col>

          <Col xs={24} lg={18}>
            <Row gutter={[16, 16]}>
              {mode === 'view' ? (
                <Col xs={24}>
                  <Card
                    title={
                      <Space wrap>
                        <span>决策链档案</span>
                        <Tag color="green">查看模式</Tag>
                      </Space>
                    }
                  >
                    {traceQuery.isLoading ? (
                      <Loading />
                    ) : viewTraceNodes.length ? (
                      <div style={{ display: 'grid', gap: 16 }}>
                        <Row gutter={[12, 12]}>
                          <Col xs={12} md={6}>
                            <Card size="small">
                              <Statistic title="已归档节点" value={archivedTraceNodes.length} />
                            </Card>
                          </Col>
                          <Col xs={12} md={6}>
                            <Card size="small">
                              <Statistic title="证据图片" value={imageEvidenceCount} />
                            </Card>
                          </Col>
                          <Col xs={24} md={12}>
                            <Card size="small">
                              <Typography.Text type="secondary">市场日型</Typography.Text>
                              <div style={{ marginTop: 8 }}>
                                <Space wrap>
                                  <Typography.Text strong>{selectedDay.day_type || '-'}</Typography.Text>
                                  {selectedDay.labels.map((label) => <Tag key={label}>{label}</Tag>)}
                                </Space>
                              </div>
                            </Card>
                          </Col>
                        </Row>

                        <div style={{ display: 'grid', gap: 12 }}>
                          {viewTraceNodes.map((node) => {
                            const hasText = hasDecisionText(node);
                            const nodeEvidenceCount = node.evidence_items?.length ?? 0;
                            return (
                              <Card
                                key={decisionNodeKey(node)}
                                size="small"
                                title={
                                  <Space wrap>
                                    <span>{node.node_title}</span>
                                    <Tag color={getDecisionNodeStatusMeta(node.status).color}>
                                      {getDecisionNodeStatusMeta(node.status).text}
                                    </Tag>
                                    <Tag>{node.target_scope}</Tag>
                                  </Space>
                                }
                                extra={<Typography.Text type="secondary">目标日：{node.target_date}</Typography.Text>}
                              >
                                {hasText ? (
                                  <Descriptions column={{ xs: 1, md: 2 }} size="small">
                                    {node.evidence_interpretation ? <Descriptions.Item label="证据解读">{node.evidence_interpretation}</Descriptions.Item> : null}
                                    {node.market_judgement ? <Descriptions.Item label="交易判断">{node.market_judgement}</Descriptions.Item> : null}
                                    {node.action_taken ? <Descriptions.Item label="实际动作">{node.action_taken}</Descriptions.Item> : null}
                                    {node.risk_note ? <Descriptions.Item label="风险备注">{node.risk_note}</Descriptions.Item> : null}
                                    {node.result_review ? <Descriptions.Item label="结果复盘">{node.result_review}</Descriptions.Item> : null}
                                  </Descriptions>
                                ) : (
                                  <Typography.Text type="secondary">
                                    未填写文字判断，已归档 {nodeEvidenceCount} 张证据
                                  </Typography.Text>
                                )}

                                {node.evidence_items?.length ? (
                                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginTop: 12 }}>
                                    {node.evidence_items.map((item) => (
                                      <div key={item.id} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, minHeight: 96 }}>
                                        <Space wrap size={4}>
                                          <Tag color={item.evidence_type === 'image' ? 'purple' : 'cyan'}>
                                            {item.evidence_type === 'image' ? '图片' : '文字'}
                                          </Tag>
                                          <Typography.Text strong style={{ fontSize: 12 }}>{item.title || '未命名证据'}</Typography.Text>
                                        </Space>
                                        {item.evidence_type === 'image' ? (
                                          <img
                                            alt={item.title || '证据图片'}
                                            src={getEvidenceImageSrc(item, station)}
                                            style={{
                                              display: 'block',
                                              width: '100%',
                                              maxHeight: 128,
                                              objectFit: 'contain',
                                              marginTop: 8,
                                              borderRadius: 4,
                                              background: '#f8fafc',
                                            }}
                                          />
                                        ) : (
                                          <Typography.Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                                            {item.content_text || '-'}
                                          </Typography.Paragraph>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : null}
                              </Card>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <Empty description="暂无归档决策节点" />
                    )}
                  </Card>
                </Col>
              ) : (
              <Col xs={24}>
                <Card
                  title={
                    <Space wrap>
                      <span>填写决策节点</span>
                      <Tag color="blue">填写模式</Tag>
                    </Space>
                  }
                >
                  {traceQuery.isLoading ? (
                    <Loading />
                  ) : selectedDecisionNode ? (
                    <div style={{ display: 'grid', gap: 12 }}>
                      <Space wrap align="center">
                        <Typography.Text type="secondary">填写内容</Typography.Text>
                        <Select
                          style={{ minWidth: 320 }}
                          value={decisionNodeKey(selectedDecisionNode)}
                          options={(selectedTraceDay?.nodes ?? []).map((node) => ({
                            label: node.node_title,
                            value: decisionNodeKey(node),
                          }))}
                          onChange={setSelectedNodeKey}
                        />
                      </Space>
                      <Card
                        size="small"
                        title={
                          <Space wrap>
                            <span>{selectedDecisionNode.node_title}</span>
                            <Tag color={getDecisionNodeStatusMeta(selectedDecisionNode.status).color}>
                              {getDecisionNodeStatusMeta(selectedDecisionNode.status).text}
                            </Tag>
                            <Tag>{selectedDecisionNode.target_scope}</Tag>
                          </Space>
                        }
                        extra={<Typography.Text type="secondary">目标日：{selectedDecisionNode.target_date}</Typography.Text>}
                      >
                        <div style={{ display: 'grid', gap: 8 }}>
                          <TextArea
                            rows={2}
                            placeholder="证据解读：当时从截图、群聊、云图里看到了什么"
                            value={(nodeDrafts[decisionNodeKey(selectedDecisionNode)] ?? nodeToDraft(selectedDecisionNode)).evidence_interpretation}
                            onChange={(event) => updateNodeDraft(selectedDecisionNode, { evidence_interpretation: event.target.value })}
                          />
                          <TextArea
                            rows={2}
                            placeholder="交易判断：电价、限电、出力、SOC、风险判断"
                            value={(nodeDrafts[decisionNodeKey(selectedDecisionNode)] ?? nodeToDraft(selectedDecisionNode)).market_judgement}
                            onChange={(event) => updateNodeDraft(selectedDecisionNode, { market_judgement: event.target.value })}
                          />
                          <TextArea
                            rows={2}
                            placeholder="实际动作：申报策略或日内调整动作"
                            value={(nodeDrafts[decisionNodeKey(selectedDecisionNode)] ?? nodeToDraft(selectedDecisionNode)).action_taken}
                            onChange={(event) => updateNodeDraft(selectedDecisionNode, { action_taken: event.target.value })}
                          />
                          <TextArea
                            rows={2}
                            placeholder="风险备注/结果复盘"
                            value={(nodeDrafts[decisionNodeKey(selectedDecisionNode)] ?? nodeToDraft(selectedDecisionNode)).risk_note}
                            onChange={(event) => updateNodeDraft(selectedDecisionNode, { risk_note: event.target.value })}
                          />
                          <Space wrap>
                            <Button
                              type="primary"
                              onClick={() => handleSaveDecisionNode(selectedDecisionNode)}
                              loading={saveDecisionNodeMutation.isPending}
                            >
                              保存草稿
                            </Button>
                            <Button
                              onClick={() => handleArchiveDecisionNode(selectedDecisionNode)}
                              loading={saveDecisionNodeMutation.isPending || archiveDecisionNodeMutation.isPending}
                            >
                              保存并归档
                            </Button>
                          </Space>
                        </div>
                        {selectedDecisionNode.evidence_items?.length ? (
                          <div style={{ marginTop: 12 }}>
                            <Typography.Text type="secondary">已添加证据</Typography.Text>
                            <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
                              {selectedDecisionNode.evidence_items.map((item) => (
                                <div key={item.id} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: 8 }}>
                                  <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
                                    <Space wrap>
                                      <Tag color={item.evidence_type === 'image' ? 'purple' : 'cyan'}>
                                        {item.evidence_type === 'image' ? '图片' : '文字'}
                                      </Tag>
                                      <Typography.Text strong>{item.title || '未命名证据'}</Typography.Text>
                                      {(item.tags ?? []).map((tag) => <Tag key={tag}>{tag}</Tag>)}
                                    </Space>
                                    <Popconfirm
                                      title="删除这条证据？"
                                      okText="删除"
                                      cancelText="取消"
                                      onConfirm={() => handleDeleteEvidence(item.id)}
                                    >
                                      <Button
                                        size="small"
                                        danger
                                        loading={deleteEvidenceMutation.isPending}
                                      >
                                        删除证据
                                      </Button>
                                    </Popconfirm>
                                  </Space>
                                  {item.content_text ? <div style={{ marginTop: 6 }}>{item.content_text}</div> : null}
                                  {item.evidence_type === 'image' ? (
                                    <div style={{ marginTop: 8 }}>
                                      <img
                                        alt={item.title || '证据图片'}
                                        src={getEvidenceImageSrc(item, station)}
                                        style={{
                                          maxWidth: 260,
                                          maxHeight: 150,
                                          width: 'auto',
                                          height: 'auto',
                                          border: '1px solid #e5e7eb',
                                          borderRadius: 6,
                                          objectFit: 'contain',
                                        }}
                                      />
                                    </div>
                                  ) : null}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f0f0', display: 'grid', gap: 8 }}>
                          <Typography.Text strong>添加证据</Typography.Text>
                          <Space wrap>
                            <Input
                              style={{ width: 160 }}
                              placeholder="证据标题"
                              value={(evidenceDrafts[decisionNodeKey(selectedDecisionNode)] ?? { title: '', content_text: '', tags: '' }).title}
                              onChange={(event) => updateEvidenceDraft(selectedDecisionNode, { title: event.target.value })}
                            />
                            <Input
                              style={{ width: 180 }}
                              placeholder="标签，逗号分隔"
                              value={(evidenceDrafts[decisionNodeKey(selectedDecisionNode)] ?? { title: '', content_text: '', tags: '' }).tags}
                              onChange={(event) => updateEvidenceDraft(selectedDecisionNode, { tags: event.target.value })}
                            />
                          </Space>
                          <TextArea
                            rows={2}
                            placeholder="粘贴微信群文字、运维反馈或手动证据说明"
                            value={(evidenceDrafts[decisionNodeKey(selectedDecisionNode)] ?? { title: '', content_text: '', tags: '' }).content_text}
                            onChange={(event) => updateEvidenceDraft(selectedDecisionNode, { content_text: event.target.value })}
                          />
                          <Space wrap>
                            <Button
                              onClick={() => handleAddTextEvidence(selectedDecisionNode)}
                              loading={addTextEvidenceMutation.isPending}
                            >
                              保存文字证据
                            </Button>
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp"
                              onChange={(event) => {
                                const file = event.target.files?.[0];
                                handleUploadImageEvidence(selectedDecisionNode, file);
                                event.target.value = '';
                              }}
                            />
                          </Space>
                        </div>
                      </Card>
                    </div>
                  ) : (
                    <Empty description="暂无决策节点" />
                  )}
                </Card>
              </Col>
              )}

              {mode === 'view' ? (
                <>
              <Col xs={24}>
                <Typography.Title level={5} style={{ margin: 0 }}>事后结果</Typography.Title>
              </Col>
              <Col xs={12} md={6}>
                <Card>
                  <Statistic title="实时均价" value={format2(selectedDay.price_summary.avg_price as number)} suffix="元/MWh" />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card>
                  <Statistic title="峰谷差" value={format2(selectedDay.price_summary.spread as number)} suffix="元/MWh" />
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card>
                  <Typography.Text type="secondary">场站运行收益</Typography.Text>
                  <div style={{ marginTop: 8 }}>
                    <MetricLine
                      label="上网电量"
                      value={format2(selectedDay.execution_summary.station_on_grid_energy_mwh as number)}
                      unit="MWh"
                    />
                    <MetricLine
                      label="预估收入"
                      value={format2(selectedDay.execution_summary.station_estimated_revenue_yuan as number)}
                      unit="元"
                    />
                    <MetricLine
                      label="收入均价"
                      value={format2(selectedDay.execution_summary.station_avg_revenue_price as number)}
                      unit="元/MWh"
                    />
                  </div>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card>
                  <Typography.Text type="secondary">配储策略收益</Typography.Text>
                  <div style={{ marginTop: 8 }}>
                    <MetricLine
                      label="充电电量"
                      value={format2(selectedDay.execution_summary.battery_charge_mwh as number)}
                      unit="MWh"
                    />
                    <MetricLine
                      label="放电电量"
                      value={format2(selectedDay.execution_summary.battery_discharge_mwh as number)}
                      unit="MWh"
                    />
                    <MetricLine
                      label="预估收入"
                      value={format2(selectedDay.execution_summary.storage_estimated_revenue_yuan as number)}
                      unit="元"
                    />
                    <MetricLine
                      label="放电均价"
                      value={format2(selectedDay.execution_summary.storage_discharge_avg_price as number)}
                      unit="元/MWh"
                    />
                  </div>
                </Card>
              </Col>

              <Col xs={24}>
                <Alert
                  type={selectedDay.deviation_summary.has_deviation ? 'warning' : 'success'}
                  showIcon
                  message={
                    <Space wrap size={[8, 4]}>
                      <Typography.Text strong>市场日型：{selectedDay.day_type}</Typography.Text>
                      {getWeatherLabels(selectedDay).map((label) => (
                        <Tag key={label} color={getWeatherTagColor(label)} style={{ marginInlineEnd: 0 }}>
                          {label}
                        </Tag>
                      ))}
                    </Space>
                  }
                  description={
                    <Typography.Text type="secondary">
                      {selectedDay.weather_overview.trading_hint ?? '天气标签不足'}
                    </Typography.Text>
                  }
                />
              </Col>

              <Col xs={24} xl={12}>
                <ChartCard title="山东现货价格曲线" option={buildPriceOption(selectedDay)} height={320} />
              </Col>
              <Col xs={24} xl={12}>
                <ChartCard title="站内出力/储能执行" option={buildExecutionOption(selectedDay)} height={320} />
              </Col>

              <Col xs={24}>
                <Card title="自动回填摘要">
                  <Descriptions column={{ xs: 1, md: 2, xl: 4 }} size="small" bordered>
                    <Descriptions.Item label="低价充电窗口">
                      {format2(selectedDay.price_summary.storage_low_price_avg as number)} 元/MWh
                      <Typography.Text type="secondary"> · {selectedDay.price_summary.storage_low_price_window ?? '-'}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="高价放电窗口">
                      {format2(selectedDay.price_summary.storage_high_price_avg as number)} 元/MWh
                      <Typography.Text type="secondary"> · {selectedDay.price_summary.storage_high_price_window ?? '-'}</Typography.Text>
                    </Descriptions.Item>
                    <Descriptions.Item label="可捕捉价差">{format2(selectedDay.price_summary.storage_capture_spread as number)} 元/MWh</Descriptions.Item>
                    <Descriptions.Item label="负价点数">{selectedDay.price_summary.negative_count ?? 0}</Descriptions.Item>
                    <Descriptions.Item label="天气口径">
                      <Tooltip title={selectedDay.weather_overview.source_note as string}>
                        <Tag color={selectedDay.weather_overview.source === 'actual' ? 'green' : 'blue'}>
                          {selectedDay.weather_overview.source_label ?? selectedDay.weather_overview.source ?? '-'}
                        </Tag>
                      </Tooltip>
                    </Descriptions.Item>
                    <Descriptions.Item label="申报电量">{format2(selectedDay.execution_summary.declared_energy_mwh as number)} MWh</Descriptions.Item>
                    <Descriptions.Item label="实际电量">{format2(selectedDay.execution_summary.actual_energy_mwh as number)} MWh</Descriptions.Item>
                    <Descriptions.Item label="充电量">{format2(selectedDay.execution_summary.battery_charge_mwh as number)} MWh</Descriptions.Item>
                    <Descriptions.Item label="放电量">{format2(selectedDay.execution_summary.battery_discharge_mwh as number)} MWh</Descriptions.Item>
                    <Descriptions.Item label="配储现货收益">{format2(selectedDay.execution_summary.storage_spot_revenue_yuan as number)} 元</Descriptions.Item>
                    <Descriptions.Item label="虚报收益">{format2(selectedDay.execution_summary.storage_virtual_recovery_yuan as number)} 元</Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
                </>
              ) : null}

              {showManualReviewPanel ? (
              <Col xs={24}>
                <Card
                  title={
                    <Space>
                      <span>人工复盘</span>
                      <Tag color={getNoteStatusMeta(selectedDay.note?.status).color}>
                        {selectedDay.note?.status === 'completed' ? '复盘已完成' : getNoteStatusMeta(selectedDay.note?.status).text}
                      </Tag>
                    </Space>
                  }
                  extra={
                    <Space>
                      {lastSavedAt ? <Typography.Text type="secondary">已保存 {lastSavedAt}</Typography.Text> : null}
                      {selectedDay.note?.status === 'completed' && !editMode ? (
                        <Button onClick={() => setEditMode(true)}>编辑复盘</Button>
                      ) : (
                        <>
                          <Button
                            onClick={handleSaveDraft}
                            loading={saveNoteMutation.isPending}
                            disabled={completeNoteMutation.isPending}
                          >
                            保存草稿
                          </Button>
                          <Button
                            type="primary"
                            onClick={handleCompleteDiary}
                            loading={completeNoteMutation.isPending}
                            disabled={saveNoteMutation.isPending}
                          >
                            完成复盘
                          </Button>
                        </>
                      )}
                    </Space>
                  }
                >
                  {selectedDay.note?.status === 'completed' && !editMode ? (
                    <Descriptions column={{ xs: 1, md: 2 }} bordered size="small">
                      <Descriptions.Item label="事前判断">{draft.preJudgement || '-'}</Descriptions.Item>
                      <Descriptions.Item label="盘中动作">{draft.intradayAction || '-'}</Descriptions.Item>
                      <Descriptions.Item label="偏差原因">{draft.deviationReason || '-'}</Descriptions.Item>
                      <Descriptions.Item label="自评分">{draft.selfScore ?? '-'}</Descriptions.Item>
                    </Descriptions>
                  ) : (
                    <Row gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <Typography.Text strong>事前判断</Typography.Text>
                        <TextArea rows={5} value={draft.preJudgement} onChange={(e) => updateDraft({ preJudgement: e.target.value })} />
                      </Col>
                      <Col xs={24} md={12}>
                        <Typography.Text strong>盘中动作</Typography.Text>
                        <TextArea rows={5} value={draft.intradayAction} onChange={(e) => updateDraft({ intradayAction: e.target.value })} />
                      </Col>
                      <Col xs={24} md={12}>
                        <Typography.Text strong>偏差原因</Typography.Text>
                        <TextArea rows={5} value={draft.deviationReason} onChange={(e) => updateDraft({ deviationReason: e.target.value })} />
                      </Col>
                      <Col xs={24} md={12}>
                        <Typography.Text strong>自评分</Typography.Text>
                        <Space.Compact style={{ marginTop: 8 }}>
                          <InputNumber min={0} max={100} value={draft.selfScore} onChange={(value) => updateDraft({ selfScore: value })} />
                          <Button disabled>分</Button>
                        </Space.Compact>
                      </Col>
                    </Row>
                  )}
                </Card>
              </Col>
              ) : null}
            </Row>
          </Col>
        </Row>
      )}
      <TradingDiaryImportModal
        open={importModalOpen}
        station={station}
        date={selectedDate}
        onClose={() => setImportModalOpen(false)}
        onImported={() => {
          setImportModalOpen(false);
          query.refetch();
        }}
      />
    </PageContainer>
  );
};

export default TradingDiaryPage;
